import { ImagePlus, LoaderCircle } from 'lucide-react'
import type { DragEvent } from 'react'
import { TopBar } from '@/app/layout/TopBar'
import { useEditSession } from '@/core/store/editSession'
import { TEST_IDS } from '@/lib/testIds'
import { RecentEdits } from '@/ui/RecentEdits'
import { ContactLinks } from '@/ui/ContactLinks'

interface EmptyStateProps {
  mobile: boolean
  onChooseImage: () => void
  onFile: (file: File) => void
}

export function EmptyState({ mobile, onChooseImage, onFile }: EmptyStateProps) {
  const isLoading = useEditSession((state) => state.isLoading)

  const onDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault()
    const file = event.dataTransfer.files[0]
    if (!file) throw new Error('Drop did not contain a file')
    onFile(file)
  }

  return (
    <div className="flex min-h-dvh flex-col bg-[radial-gradient(circle_at_top,var(--color-muted),transparent_55%)]">
      <TopBar mobile={mobile} onChooseImage={onChooseImage} />
      <main className="flex flex-1 justify-center overflow-y-auto p-5 sm:p-10">
        <div className="flex w-full max-w-4xl flex-col items-center justify-center gap-8">
          <button
            data-testid={TEST_IDS.dropZone}
            type="button"
            disabled={isLoading}
            onClick={onChooseImage}
            onDragOver={(event) => event.preventDefault()}
            onDrop={onDrop}
            className="group flex min-h-72 w-full max-w-2xl flex-col items-center justify-center rounded-3xl border border-dashed bg-background/75 p-8 text-center shadow-sm transition hover:border-foreground/40 hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 disabled:opacity-70 sm:min-h-96"
          >
            <span className="mb-5 grid size-14 place-items-center rounded-2xl bg-foreground text-background shadow-sm">
              {isLoading ? <LoaderCircle className="size-6 animate-spin" aria-hidden="true" /> : <ImagePlus className="size-6" aria-hidden="true" />}
            </span>
            <span className="text-xl font-semibold tracking-tight">{isLoading ? 'Preparing your photo…' : 'Drop a photo, paste, or tap to choose'}</span>
            <span className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">JPEG, PNG, WebP, AVIF, HEIC, GIF, or BMP. Everything stays on this device.</span>
          </button>
          <RecentEdits hideWhenEmpty />
          <ContactLinks prompt="Questions or ideas? DM me" />
        </div>
      </main>
    </div>
  )
}
